import { OptionType } from '@europrocurement/flexy-components';
import { FlexyForm, FormStructure } from '@europrocurement/flexy-form';
import { DossierOpenSearch, useOffreService, useSociete } from '@europrocurement/l2d-domain';
import { ArticlesJsonldArticleRead } from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { Button, CircularProgress, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';

export type AddProcessFormProps = {
    categoryId: number;
    dossier: DossierOpenSearch;
    onSubmit: (payload: AddProcessFormData) => void;
    onCancel: () => void;
};

export type AddProcessFormData = {
    title: string;
    articleId: number;
    prestationId: number;
};

type SubmittedProcessFormData = {
    title: string;
    article: OptionType;
    prestationId: Array<number>;
};

const AddProcessForm: React.FunctionComponent<AddProcessFormProps> = function (props) {
    const { categoryId, dossier, onSubmit, onCancel } = props;
    const xIdSociete = useSociete();
    const { articlesApi } = useOffreService();

    const [processing, setProcessing] = useState<boolean>(false);
    const [articles, setArticles] = useState<ArticlesJsonldArticleRead[]>([]);

    const prestationChoices = dossier.formalites
        .filter((formalite) => formalite.prestationId.length > 0)
        .map((formalite) => ({
            value: formalite.prestationId[0],
            label: formalite.libelle,
        }));

    useEffect(() => {
        articlesApi
            .apiArticlesGetCollection({
                xIdSociete,
                catArticleId: categoryId,
            })
            .then((res) => setArticles(res.data['hydra:member']))
            .catch((err) => console.log(err));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [articlesApi]);

    const handleSubmit: SubmitHandler<SubmittedProcessFormData> = (
        formData: SubmittedProcessFormData,
    ) => {
        setProcessing(true);
        let prestationId;

        if (prestationChoices.length === 1) {
            prestationId = prestationChoices[0].value;
        } else if (formData?.prestationId[0]) {
            prestationId = formData?.prestationId[0] as number;
        }

        if (prestationId) {
            onSubmit({
                title: formData.title,
                articleId: formData?.article?.value as number,
                prestationId,
            });
        } else {
            enqueueSnackbar('Formalité associée non trouvée', {
                variant: 'error',
            });
        }
    };

    const formStructure: FormStructure[] = [
        {
            type: 'text',
            name: 'title',
            inputlabel: 'Nom de la démarche',
            rules: {
                required: 'Ce champ est requis',
            },
        },
        ...(prestationChoices.length > 1
            ? [
                  {
                      type: 'selectItems',
                      name: 'prestationId',
                      mode: 'inline',
                      inputlabel: 'Formalité associée',
                      colInline: 6,
                      items: prestationChoices,
                      multiple: false,
                      rules: {
                          required: 'Ce champ est requis',
                      },
                  } as FormStructure,
              ]
            : []),
        {
            type: 'select',
            name: 'article',
            inputlabel: 'Article',
            options: articles.map((article) => ({
                value: article.id,
                label: article.libelle || 'article sans libelle',
            })),
            rules: {
                required: 'Ce champ est requis',
            },
        },
    ];

    const formContext = useForm<FieldValues>({
        mode: 'onChange',
    });

    const formProps = {
        formContext,
        onSubmit: handleSubmit,
        submitButton: {
            displayed: false,
        },
    };

    const submitBtnSx = {
        backgroundColor: 'primary.dark',
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '0 10px',
    };

    return (
        <Box>
            <Typography variant="l2dh6">Ajouter un enregistrement</Typography>
            <Box
                sx={{
                    padding: '26px 0',
                    display: 'flex',
                    flexDirection: 'column',
                    rowGap: '10px',
                }}
            >
                <FlexyForm
                    formStructure={formStructure}
                    {...formProps}
                />
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    columnGap: '16px',
                }}
            >
                <Button
                    variant="text"
                    sx={{
                        padding: '16px 24px',
                        alignItems: 'center',
                        color: 'primary.dark',
                    }}
                    onClick={onCancel}
                    disabled={processing}
                >
                    Annuler
                </Button>
                <Button
                    variant="contained"
                    sx={submitBtnSx}
                    onClick={formContext.handleSubmit(handleSubmit)}
                    disabled={processing}
                >
                    {processing && (
                        <CircularProgress
                            color="inherit"
                            size="16px"
                        />
                    )}
                    {!processing && (
                        <FaOptionIcon
                            icon={['fas', 'plus']}
                            sx={{ lineHeight: 0 }}
                        />
                    )}
                    Ajouter l&apos;enregistrement
                </Button>
            </Box>
        </Box>
    );
};

export default AddProcessForm;
